import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FormSavePrompt, Toast, ToastTypes, launchToast } from '../../components'
import {
    removeKey,
    storeSelected,
    syncSelected,
    useBalenaDeviceData,
    useBalenaRelease,
    useFormSaveStore,
    useTabTitle
} from '../../utils'
import { useLiveViewStateStore } from '../LiveView/LiveViewStore'
import { useGetDeviceData, useGetDeviceUuids, useGetStationByDeviceId } from '../StationView/utils/station-hooks'
import { DeviceConfig, DeviceProvider, DeviceType } from './index'
import {
    getSemver,
    getIsRunningReleaseId,
    getLiveViewAccessToken,
    getLiveViewObject,
    getLiveViewUrl,
    getShouldBeRunningReleaseId,
    useSetDeviceForceUpdate,
    useUpdateDeviceConfigMutation
} from './utils'
import { browserHistory } from '../../App'
import { DeviceTypeEnum } from '../../data/enums'

export const DeviceConfigContainer = () => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const deviceIdFromUrl = queryParams.get('deviceId')
    const [deviceId, setDeviceId] = useState(deviceIdFromUrl)
    useEffect(() => {
        setDeviceId(deviceIdFromUrl)
    }, [deviceIdFromUrl])
    const history = useHistory()
    const storedSelectedDevice = syncSelected('deviceId')
    const { setBaseURL, setIsPmView, setLiveViewObject, setStationName, setDeviceName } = useLiveViewStateStore()

    useEffect(() => {
        if (!deviceId) {
            if (storedSelectedDevice) {
                history.push(`?deviceId=${storedSelectedDevice}`)
                setDeviceId(storedSelectedDevice)
            }
        } else {
            if (storedSelectedDevice && storedSelectedDevice !== deviceId) {
                storeSelected('deviceId', deviceId)
            }
        }
    }, [deviceId, storedSelectedDevice, history])
    const [selectedDevice, setSelectedDeviceState] = useState<DeviceType | null>()
    const { data, refreshDevices, loading: loadingFetchDevices } = useGetDeviceUuids()
    const { data: deviceData, refetch, loading: loadingDeviceData } = useGetDeviceData(selectedDevice?.uuid)
    const { data: deviceStations, refetchDeviceStations } = useGetStationByDeviceId(selectedDevice?.uuid)
    useTabTitle(selectedDevice, (device) => device?.device_name || 'device')

    const { balenaDeviceData, refetchBalenaDeviceData, balenaStatus } = useBalenaDeviceData(selectedDevice?.uuid)

    const { balenaReleaseData: balenaRunningReleaseData, refetchBalenaRelease: refetchBalenaRunningRelease } =
        useBalenaRelease(getIsRunningReleaseId(balenaDeviceData))
    const { balenaReleaseData: balenaTargetRealeaseData, refetchBalenaRelease: refetchBalenaTargetRelease } =
        useBalenaRelease(getShouldBeRunningReleaseId(balenaDeviceData))

    const { updateDeviceConfig, loading: loadingSaveBasicConfig } = useUpdateDeviceConfigMutation()
    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate(() => {
        if (!selectedDevice?.uuid) return
        refetch().catch((err) => {
            console.error('Refetch failed after force update', err)
            launchToast(ToastTypes.Error, 'Force update succeeded but data refresh failed.')
        })
    })

    useEffect(() => {
        if (deviceId && data?.device) {
            const foundDevice = data.device.find((device) => device.uuid === deviceId)
            if (foundDevice) {
                setSelectedDeviceState(foundDevice as DeviceType)
            } else {
                launchToast(ToastTypes.Error, 'Cannot find The device.')
            }
        }
    }, [deviceId, data])

    //LIVE VIEW URL
    useEffect(() => {
        if (
            selectedDevice &&
            deviceData?.device[0] &&
            deviceData?.device[0].type !== DeviceTypeEnum.TERMINAL &&
            deviceData?.device[0].type !== DeviceTypeEnum.DISPLAY
        ) {
            setBaseURL(getLiveViewUrl(deviceData.device[0]) || 'FAILURE')
            setIsPmView(false)
            setLiveViewObject(getLiveViewObject(deviceData.device[0]))
            setStationName(deviceStations?.station[0]?.station_name || null)
            setDeviceName(deviceData.device[0]?.device_name || null)
            localStorage.setItem('liveViewAccessToken', getLiveViewAccessToken(deviceData.device[0]) || 'FAILURE')
        }
    }, [deviceData])

    // *********Update config *********
    const editDiffConfig = async (
        device_uuid: string | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        diff_config: any,
        resetFunc?: () => void
    ): Promise<void> => {
        if (!device_uuid || !selectedDevice?.uuid) {
            return
        }
        try {
            await updateDeviceConfig({
                device_uuid: selectedDevice.uuid,
                diff_config,
                timestamp: deviceData?.device[0].config_target_timestamp ?? ''
            })
            resetFunc?.()
            refetch()
            launchToast(ToastTypes.Success, 'Successfully updated config!')
        } catch (error) {
            launchToast(ToastTypes.Error, `${error}`)
            console.error(error)
        }
    }

    const setDeviceForceUpdateAction = (deviceUIID: string | undefined): void => {
        if (!deviceUIID) {
            launchToast(ToastTypes.Error, 'No device Selected!')
            return
        }
        setDeviceForceUpdate({ device_uuid: deviceUIID }).catch((error) => {
            console.error(error)
        })
    }

    useEffect(() => {
        if (getIsRunningReleaseId(balenaDeviceData)) {
            refetchBalenaRunningRelease()
        }
        if (getShouldBeRunningReleaseId(balenaDeviceData)) {
            refetchBalenaTargetRelease()
        }
    }, [balenaDeviceData, refetchBalenaRunningRelease, refetchBalenaTargetRelease])

    useEffect(() => {
        if (selectedDevice) {
            refetch()
            refetchDeviceStations()
            refetchBalenaDeviceData()
        }
    }, [selectedDevice, refetch, refetchDeviceStations, refetchBalenaDeviceData])

    const setSelectedDevice = (device: DeviceType | null): void => {
        const { hasChanges, resetChanges } = useFormSaveStore.getState()

        if (hasChanges) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to switch devices?')
            if (!confirmed) return
            const unblock = browserHistory.block(true)

            setTimeout(() => unblock(), 0)
            resetChanges()
        }

        if (device) {
            storeSelected('deviceId', device.uuid)
            history.push(`?deviceId=${device.uuid}`)
        } else {
            removeKey('deviceId')
            history.push(`/device`)
        }

        setSelectedDeviceState(device)
    }

    const targetCommit = getSemver(balenaTargetRealeaseData)
    const runningCommit = getSemver(balenaRunningReleaseData)
    return (
        <DeviceProvider
            context={{
                loadingFetchDevices: loadingFetchDevices,
                loadingDeviceData,
                refreshDevices: () => {
                    refreshDevices()
                        .finally(() => {
                            //TODO set success message
                        })
                        .catch((error) => {
                            launchToast(
                                ToastTypes.Error,
                                'Error: Could not fetch devices. Check the console for more details !'
                            )
                            console.error(error)
                        })
                },
                getSelectedDeviceType: () => {
                    return deviceData?.device[0].type
                },
                deviceData: deviceData?.device[0],
                selectedDevice,
                setSelectedDevice,
                devices: data?.device || [],
                deviceStations: deviceStations?.station,
                editDiffConfig,
                setDeviceForceUpdateAction,
                targetCommit,
                runningCommit,
                balenaStatus,
                balenaDeviceData: balenaDeviceData,
                loadingSaveBasicConfig,
                loadingForceUpdate
            }}
        >
            <Toast />
            <FormSavePrompt />
            <DeviceConfig />
        </DeviceProvider>
    )
}
